import { cameraBasis, type CameraState } from './camera.ts';
import type { RenderOptions, FrameStats } from './softwareTerrain.ts';
import type { RenderStateView } from '../simulation/types.ts';
import type { ViewMode } from './viewModes.ts';

const MODE: Record<ViewMode, number> = {
  terrain: 0, height: 1, slope: 2, water: 3, sediment: 4,
  velocity: 5, erosion: 6, deposition: 7, hardness: 8, soil: 9,
};

const SHADER = /* wgsl */ `
struct Cell {
  height:f32, bedrock:f32, soil:f32, water:f32, sediment:f32,
  velU:f32, velV:f32, fluxX:f32, fluxY:f32, erosion:f32,
  deposition:f32, hardness:f32, windExport:f32, flowAccum:f32, groundwater:f32,
};
struct Scene {
  eye:vec4<f32>, right:vec4<f32>, up:vec4<f32>, forward:vec4<f32>,
  sizes:vec4<f32>, display:vec4<f32>,
};
struct Vary { @builtin(position) pos:vec4<f32>, @location(0) world:vec3<f32>,
  @location(1) normal:vec3<f32>, @location(2) data0:vec4<f32>, @location(3) data1:vec4<f32>, @location(4) flowAccum:f32 };
@group(0) @binding(0) var<storage,read> cells:array<Cell>;
@group(0) @binding(1) var<uniform> scene:Scene;

fn sampleHeight(x:i32, y:i32, n:i32)->f32 {
  let xx=clamp(x,0,n-1); let yy=clamp(y,0,n-1);
  return cells[u32(yy*n+xx)].height / scene.sizes.z;
}
@vertex fn vs(@builtin(vertex_index) vi:u32)->Vary {
  let n=u32(scene.sizes.x); let q=vi/6u; let corner=vi%6u;
  let cx=q%(n-1u); let cy=q/(n-1u);
  var ox=0u; var oy=0u;
  if (corner==1u || corner==4u || corner==5u) { ox=1u; }
  if (corner==2u || corner==3u || corner==5u) { oy=1u; }
  let x=cx+ox; let y=cy+oy; let idx=y*n+x; let c=cells[idx];
  let inv=1.0/f32(n-1u); let world=vec3<f32>(f32(x)*inv,c.height/scene.sizes.z,f32(y)*inv);
  let hL=sampleHeight(i32(x)-1,i32(y),i32(n)); let hR=sampleHeight(i32(x)+1,i32(y),i32(n));
  let hD=sampleHeight(i32(x),i32(y)-1,i32(n)); let hU=sampleHeight(i32(x),i32(y)+1,i32(n));
  let normal=normalize(vec3<f32>(hL-hR,2.0*inv,hD-hU));
  let rel=world-scene.eye.xyz; let depth=max(0.001,dot(rel,scene.forward.xyz));
  let px=dot(rel,scene.right.xyz)/(scene.right.w*scene.up.w);
  let py=dot(rel,scene.up.xyz)/scene.right.w;
  let near=0.01; let far=20.0;
  var out:Vary;
  out.pos=vec4<f32>(px,py,(far*depth-far*near)/(far-near),depth);
  out.world=world; out.normal=normal;
  out.data0=vec4<f32>(c.water,c.sediment,length(vec2<f32>(c.velU,c.velV)),c.erosion);
  out.data1=vec4<f32>(c.deposition,c.hardness,c.soil,c.height/scene.sizes.z);
  out.flowAccum=c.flowAccum;
  return out;
}
fn ramp(t0:f32)->vec3<f32> {
  let t=clamp(t0,0.0,1.0);
  let low=vec3<f32>(0.78,0.75,0.60); let mid=vec3<f32>(0.43,0.48,0.30); let high=vec3<f32>(0.72,0.69,0.66);
  return select(mix(low,mid,t*2.0),mix(mid,high,(t-0.5)*2.0),t>0.5);
}
fn heat(t:f32)->vec3<f32> { return mix(vec3<f32>(0.10,0.18,0.28),vec3<f32>(0.95,0.62,0.18),clamp(t,0.0,1.0)); }
@fragment fn fs(v:Vary)->@location(0) vec4<f32> {
  let mode=i32(scene.display.x+0.5); let light=0.35+0.65*max(0.0,dot(normalize(v.normal),normalize(vec3<f32>(-0.55,0.72,-0.42))));
  var color=ramp(v.data1.w)*light;
  if (mode==1) { color=ramp(v.data1.w); }
  if (mode==2) { color=heat(1.0-v.normal.y); }
  if (mode==3) { color=mix(vec3<f32>(0.88),vec3<f32>(0.04,0.30,0.58),clamp(sqrt(v.data0.x)*0.8,0.0,1.0)); }
  if (mode==4) { color=heat(v.data0.y*2.0); }
  if (mode==5) { color=heat(v.data0.z/8.0); }
  if (mode==6) { color=heat(v.data0.w*8.0); }
  if (mode==7) { color=heat(v.data1.x*8.0); }
  if (mode==8) { color=vec3<f32>(clamp(v.data1.y/4.0,0.0,1.0)); }
  if (mode==9) { color=mix(vec3<f32>(0.31,0.23,0.15),vec3<f32>(0.78,0.64,0.35),clamp(v.data1.z/20.0,0.0,1.0))*light; }
  if (mode==0 && scene.display.y>0.5) {
    // Ordinary rain-sheet flow becomes visible only at centimetre scale. A
    // strongly accumulated channel gets a lower threshold, so hiding the odd
    // blue mountain film does not erase real streams.
    let channel=clamp(log2(max(1.0,v.flowAccum))/8.0,0.0,1.0);
    let wet=smoothstep(mix(0.02,0.004,channel),mix(0.10,0.025,channel),v.data0.x);
    color=mix(color,vec3<f32>(0.04,0.28,0.52),wet*clamp(0.22+sqrt(v.data0.x)*0.2,0.0,0.82));
  }
  return vec4<f32>(color*scene.display.z,1.0);
}`;

export class GpuTerrainRenderer {
  private context: GPUCanvasContext;
  private pipeline: GPURenderPipeline;
  private uniform: GPUBuffer;
  private depth: GPUTexture | null = null;
  private groups = new WeakMap<GPUBuffer, GPUBindGroup>();
  private values = new Float32Array(24);
  private lastWidth = 0;
  private lastHeight = 0;

  constructor(private canvas: HTMLCanvasElement, private device: GPUDevice) {
    const context = canvas.getContext('webgpu');
    if (!context) throw new Error('WebGPU canvas context unavailable');
    this.context = context;
    const format = navigator.gpu.getPreferredCanvasFormat();
    context.configure({ device, format, alphaMode: 'opaque' });
    this.uniform = device.createBuffer({ size: 96, usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST });
    this.pipeline = device.createRenderPipeline({
      layout: 'auto', vertex: { module: device.createShaderModule({ code: SHADER }), entryPoint: 'vs' },
      fragment: { module: device.createShaderModule({ code: SHADER }), entryPoint: 'fs', targets: [{ format }] },
      // Both winding orders are intentionally accepted. The height field is
      // viewed from above in normal use, while low orbit angles may expose its
      // reverse winding near the horizon; culling the wrong side makes the
      // entire regular grid disappear on those cameras.
      primitive: { topology: 'triangle-list', cullMode: 'none' },
      depthStencil: { format: 'depth24plus', depthWriteEnabled: true, depthCompare: 'less' },
    });
  }

  private resize(): void {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = Math.max(1, Math.round(this.canvas.clientWidth * dpr));
    const h = Math.max(1, Math.round(this.canvas.clientHeight * dpr));
    if (w === this.lastWidth && h === this.lastHeight) return;
    this.canvas.width = w; this.canvas.height = h; this.lastWidth = w; this.lastHeight = h;
    this.depth?.destroy();
    this.depth = this.device.createTexture({ size: [w, h], format: 'depth24plus', usage: GPUTextureUsage.RENDER_ATTACHMENT });
  }

  render(view: RenderStateView, camera: CameraState, opts: RenderOptions): FrameStats {
    const t0 = performance.now(); this.resize();
    if (!view.stateBuffer || !this.depth) return { renderMs: 0, width: 0, height: 0 };
    const b = cameraBasis(camera, this.lastWidth / this.lastHeight); const v = this.values;
    v.set([...b.eye, 0], 0); v.set([...b.right, b.halfTan], 4); v.set([...b.up, b.aspect], 8); v.set([...b.forward, 0], 12);
    v.set([view.width, opts.worldExtent, opts.maxHeight, 0], 16);
    v.set([MODE[opts.mode], opts.showWater ? 1 : 0, opts.exposure, opts.gridStep], 20);
    this.device.queue.writeBuffer(this.uniform, 0, v);
    let group = this.groups.get(view.stateBuffer);
    if (!group) { group = this.device.createBindGroup({ layout: this.pipeline.getBindGroupLayout(0), entries: [
      { binding: 0, resource: { buffer: view.stateBuffer } }, { binding: 1, resource: { buffer: this.uniform } },
    ]}); this.groups.set(view.stateBuffer, group); }
    const enc = this.device.createCommandEncoder();
    const pass = enc.beginRenderPass({ colorAttachments: [{ view: this.context.getCurrentTexture().createView(), clearValue: { r: 0.91, g: 0.90, b: 0.87, a: 1 }, loadOp: 'clear', storeOp: 'store' }], depthStencilAttachment: { view: this.depth.createView(), depthClearValue: 1, depthLoadOp: 'clear', depthStoreOp: 'discard' } });
    pass.setPipeline(this.pipeline); pass.setBindGroup(0, group); pass.draw((view.width - 1) * (view.height - 1) * 6); pass.end();
    this.device.queue.submit([enc.finish()]);
    return { renderMs: performance.now() - t0, width: this.lastWidth, height: this.lastHeight };
  }
  destroy(): void { this.depth?.destroy(); this.uniform.destroy(); this.context.unconfigure(); }
}
